import { formatLeagueDateTime } from "utils/league-datetime";

// Gestión area that may send and see broadcasts (gate G1). Page, route and drawer all read this.
export const PUSH_AREA = "manager";
export const PUSH_TITLE_MAX = 65;
export const PUSH_BODY_MAX = 240;
export const PUSH_BLOCK_REASONS = ["no_organizer_id", "push_not_configured", "rate_limited"];
export const PUSH_STATUSES = ["queued", "sending", "sent", "failed"];

export const emptyBroadcastForm = () => ({ title: "", body: "" });

// Characters as the API counts them (PHP mb_strlen = Unicode code points), not UTF-16 units.
export const pushCharCount = (value) => Array.from(String(value ?? "")).length;

const positiveInt = (value, fallback) => (Number.isInteger(value) && value > 0 ? value : fallback);

// limits: the `limits` object of the API (may be missing while loading).
export const resolvePushLimits = (limits) => ({
  titleMax: positiveInt(limits?.title_max, PUSH_TITLE_MAX),
  bodyMax: positiveInt(limits?.body_max, PUSH_BODY_MAX),
  perHour: Number.isInteger(limits?.per_hour) ? limits.per_hour : null,
  remaining: Number.isInteger(limits?.remaining) ? limits.remaining : null,
  retryAt: limits?.retry_at || null,
});

// Returns { field: { id, values? } } for each invalid field; {} means valid. Values are trimmed first.
export const validateBroadcastForm = (form, limits) => {
  const { titleMax, bodyMax } = resolvePushLimits(limits);
  const errors = {};
  const check = (field, max) => {
    const value = String(form?.[field] ?? "").trim();
    if (value === "") errors[field] = { id: "gestion.push.form.required" };
    else if (pushCharCount(value) > max) errors[field] = { id: "gestion.push.form.tooLong", values: { max } };
  };
  check("title", titleMax);
  check("body", bodyMax);
  return errors;
};

// { id, values? } for a blocking reason, or null for an unknown or empty reason.
export const pushBlockedMessage = (reason, limits) => {
  if (!PUSH_BLOCK_REASONS.includes(reason)) return null;
  if (reason !== "rate_limited") return { id: `gestion.push.blocked.${reason}` };
  const { perHour, retryAt } = resolvePushLimits(limits);
  if (!retryAt || perHour === null) return { id: "gestion.push.blocked.rate_limited_no_time" };
  return {
    id: "gestion.push.blocked.rate_limited",
    values: { perHour, time: formatLeagueDateTime(retryAt, undefined, "HH:mm") },
  };
};

// Maps a failed POST (axios error) to { id, values? }, or null (the caller shows gestion.error.generic).
// limits: the page's current limits; the 429 body only carries retry_at.
export const pushErrorMessage = (error, limits) => {
  const data = error?.response?.data;
  if (!PUSH_BLOCK_REASONS.includes(data?.code)) return null;
  return pushBlockedMessage(data.code, { ...(limits || {}), retry_at: data.retry_at ?? limits?.retry_at ?? null });
};

export const pushStatusLabelId = (status) =>
  PUSH_STATUSES.includes(status) ? `gestion.push.status.${status}` : "gestion.push.status.unknown";

// MUI Chip colors.
export const pushStatusColor = (status) =>
  ({ queued: "default", sending: "info", sent: "success", failed: "error" })[status] ?? "default";
